import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import { alpha, useTheme } from '@mui/material/styles'
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded'
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { systemValueCodeLabel } from '../../../../generic/systemValues/systemValueLabel'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import { coverageLabel } from '../coverageLabel'
import { riskLabel } from '../riskLabel'
import { raisedColor } from '../detailSurfaces'
import { CONTROL_RADIUS } from '../detailControls'
import { operationNameLabel, operationLabelOf } from '../operationNameLabel'
import { localDayOf } from '../detailDates'
import { fetchConsumedMaterial } from '../detailApi'
import { isBlank, EMPTY_DASH, ColumnHeader } from '../detailShared'

// RN8: an absent figure is a dash, never a blank cell.
const orDash = (value) => (isBlank(value) ? EMPTY_DASH : value)

// `2026-06-12` parsed as an instant is UTC midnight and reads as the 11th west of Greenwich: the
// day goes to the formatter as a LOCAL date.
const localDayDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(localDayOf(value))
  // An instant (with time) keeps its own value: the formatter already shows it in local time.
  return match && localDayOf(value) === value ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : value
}

const EMPTY_CONSUMED_PAGE = { content: [], totalElements: 0, page: 0, size: 20 }

const NEEDS = 'needs'
const ASSIGNED = 'assigned'

// RN49: PERSISTED/INFERRED both resolve to "the parent operation this child serves" — only
// UNDETERMINED gets its own explicit "not determined" key (never a guessed operation, RN49).
const originText = (child, t_) => {
  if (child.originProvenance === 'UNDETERMINED') {
    return t_('Origin operation not determined')
  }
  // Mismo par interpolado que en el indicador de Estado, y aquí SIN ninguna guarda: con un
  // `jobOrder` nulo —que es legítimo— esto pintaba "null - Drill-1". La regla vive en
  // `operationNameLabel`, una sola vez para los dos sitios.
  // El fallback NO es "origen no determinado": eso significa otra cosa (RN49, provenance
  // UNDETERMINED, arriba). Aquí el origen SÍ está determinado; lo que no tiene es nombre.
  const label = operationNameLabel(child.originJobReference, 'Unnamed operation')
  return label.text || t_(label.key, label.values)
}

const childProgressText = (child, t_) =>
  (isBlank(child.progressPercent) ? t_('Progress not computable') : `${child.progressPercent}%`)

// Pasada 18 (§6.2, mockup 04-materiales.png): the coverage/status pill, one shared shape for
// every enum-driven badge of this block (child status, coverage) — colour always a repeat of the
// text next to it, never its substitute.
const Pill = ({ label, color = 'text.secondary' }) => (
  <Chip
    size='small'
    label={label}
    sx={(theme) => {
      const resolved = color.includes('.') ? color.split('.').reduce((acc, key) => acc[key], theme.palette) : color
      return { fontWeight: 700, color: resolved, backgroundColor: alpha(resolved, 0.12), border: `1px solid ${alpha(resolved, 0.3)}` }
    }}
  />
)

// CU-L6/CU-L7/CU-L8 (T6) — Materials block: two tabs, Needs (dependencies + requirements) and
// Assigned (real consumption). Read-only throughout (RN11/RN56/S189): no button here reserves,
// assigns, releases or requests material, and no button launches a missing child order (S264).
const MaterialsBlock = ({
  fabricationOrderId,
  header = {},
  childOrders = [],
  materialRequirements = [],
  consumedMaterial: firstConsumedPage = EMPTY_CONSUMED_PAGE,
  onNavigateToOrder = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const { formatDate, formatDateTime } = useDateFormatter()
  const [tab, setTab] = useState(NEEDS)
  // N9 is paginated: the Dialog reads page 0, the next pages are read here and appended.
  const [consumedMaterial, setConsumedMaterial] = useState(firstConsumedPage)
  const [loadingMoreConsumed, setLoadingMoreConsumed] = useState(false)
  const [consumedLoadFailed, setConsumedLoadFailed] = useState(false)
  useEffect(() => { setConsumedMaterial(firstConsumedPage) }, [firstConsumedPage])
  const loadMoreConsumed = () => {
    setLoadingMoreConsumed(true)
    setConsumedLoadFailed(false)
    fetchConsumedMaterial(fabricationOrderId, consumedMaterial.page + 1)
      .then((next) => setConsumedMaterial((prev) => ({ ...next, content: [...prev.content, ...next.content] })))
      .catch(() => setConsumedLoadFailed(true))
      .finally(() => setLoadingMoreConsumed(false))
  }
  const hasMoreConsumed = consumedMaterial.content.length < consumedMaterial.totalElements

  const parent = header.parentFabricationOrder || {}
  const hasParent = Object.keys(parent).length > 0
  const hasDependencies = hasParent || childOrders.length > 0
  // P-4/RN52: a closed order presents its requirement rows as historical figures with no missing
  // quantity and no live coverage vocabulary — Sun already stops declaring risk on children for a
  // closed order (S245), this block additionally suppresses the requirement-side wording here.
  const isClosed = header.status === 'CLOSED'

  const cardSx = {
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
    backgroundColor: raisedColor(theme),
    overflow: 'hidden'
  }

  return (
    <Box data-testid='materials-block'>
      <Stack direction='row' spacing={1.5} data-testid='materials-tabs' sx={{ mb: 2.5 }}>
        <Button
          data-testid='materials-tab-needs'
          variant={tab === NEEDS ? 'contained' : 'outlined'}
          onClick={() => setTab(NEEDS)}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: CONTROL_RADIUS }}
        >
          {t_('Needs')}
        </Button>
        <Button
          data-testid='materials-tab-assigned'
          variant={tab === ASSIGNED ? 'contained' : 'outlined'}
          onClick={() => setTab(ASSIGNED)}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: CONTROL_RADIUS }}
        >
          {t_('Assigned')}
        </Button>
      </Stack>

      {tab === NEEDS && (
        <Stack spacing={3}>
          <Box data-testid='materials-dependencies' sx={cardSx}>
            <Box sx={{ px: 2.5, py: 1.25, backgroundColor: raisedColor(theme), borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.08)}` }}>
              <Typography variant='caption' sx={{ fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase' }}>{t_('Depends on')}</Typography>
            </Box>
            <Box sx={{ p: 2.5 }}>
              {!hasDependencies && (
                <Typography data-testid='materials-no-dependencies' variant='body2' sx={{ color: 'text.secondary' }}>
                  {t_('This order does not depend on any other')}
                </Typography>
              )}

              {hasParent && (
                <Stack direction='row' spacing={1.5} alignItems='center' data-testid='materials-parent' sx={{ mb: childOrders.length > 0 ? 2 : 0 }}>
                  <ArrowUpwardRounded fontSize='small' sx={{ color: 'text.secondary' }} />
                  <Typography component='span' variant='body2' sx={{ fontWeight: 700, flexGrow: 1 }}>{parent.code}</Typography>
                  <Button data-testid='materials-parent-detail' onClick={() => onNavigateToOrder(parent.id)} size='small' variant='outlined' sx={{ textTransform: 'none', borderRadius: CONTROL_RADIUS }}>
                    {t_('View detail')}
                  </Button>
                </Stack>
              )}

              {childOrders.length > 0 && (
                <TableContainer sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}` }}>
                  <Table size='small' aria-label={t_('Depends on')}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: raisedColor(theme) }}>
                        <TableCell><ColumnHeader>{t_('Order')}</ColumnHeader></TableCell>
                        <TableCell><ColumnHeader>{t_('Operation')}</ColumnHeader></TableCell>
                        <TableCell><ColumnHeader>{t_('Quantity')}</ColumnHeader></TableCell>
                        <TableCell><ColumnHeader>{t_('Status')}</ColumnHeader></TableCell>
                        <TableCell><ColumnHeader>{t_('Progress')}</ColumnHeader></TableCell>
                        <TableCell><ColumnHeader>{t_('Delivery')}</ColumnHeader></TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {childOrders.map((child) => {
                        const risk = riskLabel(child.risk)
                        const atRisk = child.risk?.status === 'AT_RISK'
                        return (
                          <React.Fragment key={child.fabricationOrderId}>
                            <TableRow
                              data-testid={`materials-child-${child.fabricationOrderId}`}
                              hover
                              sx={{
                                // §6.2: an at-risk child gets a red background AND a red border on
                                // its left edge, plus its warning text below — never colour alone
                                // (the border repeats the fill).
                                backgroundColor: atRisk ? alpha(theme.palette.error.main, 0.08) : 'transparent',
                                '& > td:first-of-type': atRisk ? { borderLeft: `3px solid ${theme.palette.error.main}` } : {}
                              }}
                            >
                              <TableCell>
                                <Typography component='span' variant='body2' sx={{ fontWeight: 700 }}>{orDash(child.code)}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography data-testid={`materials-child-origin-${child.fabricationOrderId}`} component='span' variant='body2' sx={{ color: 'text.secondary' }}>
                                  {originText(child, t_)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography component='span' variant='body2'>{orDash(child.quantity)}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography component='span' variant='body2'>{systemValueCodeLabel(t_, 'FabricationOrderGlobalStatusEnum', child.status)}</Typography>
                              </TableCell>
                              <TableCell sx={{ minWidth: 140 }}>
                                <Stack direction='row' spacing={1} alignItems='center'>
                                  {/* RN21: a progress that cannot be computed has no bar — a bar at 0 reads as 0%. */}
                                  {!isBlank(child.progressPercent) && (
                                    <LinearProgress
                                      variant='determinate'
                                      value={Math.min(100, Math.max(0, Number(child.progressPercent)))}
                                      sx={{ flexGrow: 1, height: 6, borderRadius: 999, backgroundColor: alpha(theme.palette.text.primary, 0.12) }}
                                    />
                                  )}
                                  <Typography data-testid={`materials-child-progress-${child.fabricationOrderId}`} variant='caption' sx={{ color: 'text.secondary' }}>
                                    {childProgressText(child, t_)}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography data-testid='materials-child-delivery' component='span' variant='body2'>{isBlank(child.deliveryDate) ? EMPTY_DASH : formatDate(localDayDate(child.deliveryDate))}</Typography>
                              </TableCell>
                              <TableCell align='right'>
                                <Button
                                  data-testid={`materials-child-detail-${child.fabricationOrderId}`}
                                  onClick={() => onNavigateToOrder(child.fabricationOrderId)}
                                  size='small'
                                  variant='outlined'
                                  sx={{ textTransform: 'none', borderRadius: CONTROL_RADIUS, minWidth: 0 }}
                                >
                                  {t_('View detail')}
                                </Button>
                              </TableCell>
                            </TableRow>
                            {risk.key && (
                              <TableRow sx={{ backgroundColor: atRisk ? alpha(theme.palette.error.main, 0.04) : 'transparent' }}>
                                <TableCell colSpan={7} sx={{ borderTop: 'none', pt: 0 }}>
                                  <Stack direction='row' spacing={0.75} alignItems='center'>
                                    <WarningAmberRounded fontSize='small' sx={{ color: 'error.main' }} />
                                    <Typography data-testid={`materials-child-risk-${child.fabricationOrderId}`} variant='caption' sx={{ color: 'error.main', fontWeight: 700 }}>
                                      {t_(risk.key, risk.values)}
                                    </Typography>
                                  </Stack>
                                  {atRisk && (
                                    <Typography data-testid={`materials-child-risk-compromise-${child.fabricationOrderId}`} variant='caption' sx={{ display: 'block', color: 'error.main' }}>
                                      {t_('Compromises this order')}
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Box>

          <Box data-testid='materials-requirements' sx={cardSx}>
            <Box sx={{ px: 2.5, py: 1.25, backgroundColor: raisedColor(theme), borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.08)}` }}>
              <Typography variant='caption' sx={{ fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase' }}>{t_('Material')}</Typography>
            </Box>
            <Box sx={{ p: materialRequirements.length === 0 ? 2.5 : 0 }}>
              {materialRequirements.length === 0 && (
                <Typography data-testid='materials-no-requirements' variant='body2' sx={{ color: 'text.secondary' }}>
                  {t_('This order does not require material')}
                </Typography>
              )}

              {materialRequirements.length > 0 && (
                <TableContainer>
                  <Table size='small' aria-label={t_('Material')}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: raisedColor(theme) }}>
                        <TableCell><ColumnHeader>{t_('Material')}</ColumnHeader></TableCell>
                        <TableCell><ColumnHeader>{t_('Required')}</ColumnHeader></TableCell>
                        <TableCell><ColumnHeader>{t_('Consumed')}</ColumnHeader></TableCell>
                        <TableCell><ColumnHeader>{t_('Available')}</ColumnHeader></TableCell>
                        {!isClosed && <TableCell><ColumnHeader>{t_('Missing')}</ColumnHeader></TableCell>}
                        <TableCell><ColumnHeader>{t_('Coverage')}</ColumnHeader></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {materialRequirements.map((row, index) => {
                        const resource = row.resource || {}
                        const operation = row.operation || {}
                        const coverage = coverageLabel(row, (status) => systemValueCodeLabel(t_, 'FabricationOrderGlobalStatusEnum', status) || EMPTY_DASH)
                        const coveringOrder = row.coveringFabricationOrder || {}
                        const coveringRisk = riskLabel(coveringOrder.risk)
                        const hasSecondUnit = !isBlank(row.requiredSecondUnit) && !isBlank(row.secondUnitLabel)
                        const coverageColor = isClosed ? 'text.secondary' : (row.coverage === 'MISSING' ? 'error.main' : row.coverage === 'COVERED_BY_ORDER' ? 'warning.main' : row.coverage === 'COVERED' ? 'success.main' : 'text.secondary')

                        return (
                          <TableRow key={`${resource.id}-${operation.jobReferenceId}-${index}`} data-testid={`materials-requirement-${index}`} hover>
                            <TableCell>
                              <Typography component='span' variant='body2' sx={{ fontWeight: 700, display: 'block' }}>{orDash(resource.name)}</Typography>
                              <Typography component='span' variant='caption' sx={{ color: 'text.secondary' }}>{orDash(operation.name)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography data-testid={`materials-requirement-required-${index}`} variant='body2'>
                                {/* `requiredQuantity` y `unit` son `number|null` y `string|null` por
                                    contrato (N8): interpolarlos a pelo imprime "null null". La
                                    cantidad ausente se dice con la clave de ausencia ('Not reported'
                                    desde el addendum 30 A, antes 'Pending'), como en el resto de la
                                    ficha, y la unidad ausente sencillamente no se escribe —
                                    inventarle una sería peor que no ponerla. */}
                                {isBlank(row.requiredQuantity) ? t_('Not reported') : `${row.requiredQuantity}${isBlank(row.unit) ? '' : ` ${row.unit}`}`}
                                {hasSecondUnit && ` (${row.requiredSecondUnit} ${row.secondUnitLabel})`}
                              </Typography>
                              <Typography data-testid={`materials-requirement-required-note-${index}`} variant='caption' sx={{ color: 'text.secondary' }}>
                                {t_('Calculated on the order quantity')}
                              </Typography>
                              {row.sharedStock && (
                                <Typography data-testid={`materials-requirement-shared-stock-${index}`} variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
                                  {t_('Shared stock with other orders')}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography data-testid={`materials-requirement-mounted-${index}`} variant='body2'>{orDash(row.mountedQuantity)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography data-testid={`materials-requirement-available-${index}`} variant='body2'>
                                {isBlank(row.availableQuantity) ? t_('Available stock could not be read') : row.availableQuantity}
                              </Typography>
                            </TableCell>
                            {!isClosed && (
                              <TableCell>
                                <Typography data-testid={`materials-requirement-missing-${index}`} variant='body2'>{orDash(row.missingQuantity)}</Typography>
                              </TableCell>
                            )}
                            <TableCell>
                              <Box data-testid={`materials-requirement-coverage-${index}`}>
                                <Pill label={isClosed ? t_('Order closed') : (coverage.key ? t_(coverage.key, coverage.values) : EMPTY_DASH)} color={coverageColor} />
                                {!isClosed && row.coverage === 'COVERED_BY_ORDER' && coveringRisk.key && (
                                  <Typography data-testid={`materials-requirement-covering-risk-${index}`} variant='caption' sx={{ display: 'block', mt: 0.5, color: 'warning.main' }}>
                                    {t_(coveringRisk.key, coveringRisk.values)}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Box>
        </Stack>
      )}

      {tab === ASSIGNED && (
        <Box data-testid='materials-consumed' sx={cardSx}>
          <Box sx={{ p: consumedMaterial.content.length === 0 ? 2.5 : 0 }}>
            {consumedMaterial.content.length === 0 && (
              <Typography data-testid='materials-no-consumed' variant='body2' sx={{ color: 'text.secondary' }}>
                {t_('This order has not consumed material yet')}
              </Typography>
            )}
            {consumedMaterial.content.length > 0 && (
              <TableContainer>
                <Table size='small' aria-label={t_('Assigned')}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: raisedColor(theme) }}>
                      <TableCell><ColumnHeader>{t_('Material')}</ColumnHeader></TableCell>
                      <TableCell><ColumnHeader>{t_('Batch')}</ColumnHeader></TableCell>
                      <TableCell><ColumnHeader>{t_('Origin')}</ColumnHeader></TableCell>
                      <TableCell><ColumnHeader>{t_('Operation')}</ColumnHeader></TableCell>
                      <TableCell><ColumnHeader>{t_('Part')}</ColumnHeader></TableCell>
                      <TableCell><ColumnHeader>{t_('Consumed')}</ColumnHeader></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {consumedMaterial.content.map((row, index) => {
                      const resource = row.resource || {}
                      const source = row.source || {}
                      const operation = row.operation || {}
                      const part = row.part || {}
                      return (
                        <TableRow key={`${row.rawMaterialId}-${index}`} data-testid={`materials-consumed-${index}`} hover>
                          <TableCell>
                            <Typography component='span' variant='body2' sx={{ fontWeight: 700 }}>{orDash(resource.name)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography component='span' variant='body2'>{orDash(row.batch)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography component='span' variant='body2' sx={{ color: 'text.secondary' }}>{orDash(source.label)}</Typography>
                          </TableCell>
                          <TableCell>
                            {/* S355 — ADDENDUM 19 (E25) B: QUINTA instancia del nombre equivocado.
                                N9 (material consumido) ya sirve `code` y un `name` propio, así que
                                leer `name` a pelo dejaría esta celda vacía. Este bloque está OCULTO
                                por E21 y por eso no se ve hoy — razón de más para arreglarlo: vuelve
                                algún día, y volvería con el defecto si nadie lo toca.
                                ⚠️ La celda de REQUERIMIENTOS de arriba NO se cambia: N8 sigue
                                sirviendo `{jobOrder, name}` y ahí `name` ES el nombre. Cambiarla
                                "por coherencia" crearía el mismo defecto del revés. */}
                            <Typography component='span' variant='body2'>{orDash(operationLabelOf(operation))}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography component='span' variant='body2'>{orDash(part.code)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography component='span' variant='body2' sx={{ color: 'text.secondary' }}>{isBlank(row.consumedAt) ? EMPTY_DASH : formatDateTime(row.consumedAt)}</Typography>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
          {hasMoreConsumed && (
            <Stack direction='row' spacing={2} alignItems='center' sx={{ p: 1.5 }}>
              <Button data-testid='materials-consumed-load-more' size='small' variant='outlined' onClick={loadMoreConsumed} disabled={loadingMoreConsumed} sx={{ textTransform: 'none', borderRadius: CONTROL_RADIUS, minHeight: 44 }}>
                {loadingMoreConsumed ? t_('Loading...') : t_('Load more')}
              </Button>
              {consumedLoadFailed && (
                <Typography data-testid='materials-consumed-load-more-error' variant='body2' sx={{ color: 'error.main' }}>{t_('Could not load this block')}</Typography>
              )}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  )
}

export default MaterialsBlock
